// pages/DeveloperConsole.js
import React, { useState } from "react";
import {
  Container,
  Grid,
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Divider,
  IconButton,
} from "@mui/material";
import {
  Code,
  CloudQueue,
  Terminal,
  Insights,
  Api,
  Memory,
  Bolt,
  Refresh,
  Chat,
} from "@mui/icons-material";
import { motion } from "framer-motion";

const DeveloperConsole = () => {
  const [logs, setLogs] = useState([
    "[INFO] Build completed successfully at 02:31 AM",
    "[INFO] API server running on port 8080",
    "[INFO] Database synced successfully",
  ]);
  const [command, setCommand] = useState("");

  const handleRunCommand = () => {
    if (command.trim()) {
      setLogs((prev) => [...prev, `[USER] ${command}`]);
      setCommand("");
      setTimeout(() => {
        setLogs((prev) => [...prev, `[SYSTEM] Executed: ${command}`]);
      }, 700);
    }
  };

  const motionCard = {
    whileHover: { y: -5, scale: 1.02 },
    transition: { duration: 0.3 },
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #0a1128 0%, #1a1f3d 60%, #0a1128 100%)",
        color: "white",
        py: 10,
      }}
    >
      <Container maxWidth="xl">
        <Typography
          variant="h2"
          align="center"
          sx={{
            fontWeight: 900,
            mb: 6,
            background:
              "linear-gradient(135deg, #8b0000, #ff4d4d, #ff9999)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            color: "transparent",
            letterSpacing: "-0.03em",
          }}
        >
          Developer Console
        </Typography>

        {/* Metrics Overview */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          {[
            {
              title: "API Calls Today",
              value: "12,487",
              icon: <Api />,
            },
            {
              title: "Build Status",
              value: "✅ Passed",
              icon: <Code />,
            },
            {
              title: "Server Uptime",
              value: "99.98%",
              icon: <CloudQueue />,
            },
            {
              title: "Memory Usage",
              value: "2.1 GB / 8 GB",
              icon: <Memory />,
            },
          ].map((stat, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <motion.div {...motionCard}>
                <Card
                  sx={{
                    background:
                      "linear-gradient(135deg, rgba(26,31,61,0.85), rgba(10,17,40,0.9))",
                    backdropFilter: "blur(12px)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 4,
                    color: "white",
                    textAlign: "center",
                    py: 4,
                    height: "100%",
                    boxShadow: "0 8px 24px rgba(255,77,77,0.1)",
                  }}
                >
                  <CardContent>
                    <Box sx={{ fontSize: 48, color: "#ff4d4d", mb: 1 }}>
                      {stat.icon}
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {stat.title}
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 1, color: "#ff9999" }}>
                      {stat.value}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Terminal + AI Assistant */}
        <Grid container spacing={4}>
          {/* Developer Terminal */}
          <Grid item xs={12} md={7}>
            <motion.div {...motionCard}>
              <Card
                sx={{
                  background:
                    "linear-gradient(135deg, rgba(10,17,40,0.9), rgba(26,31,61,0.95))",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 4,
                  height: 500,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardContent sx={{ flex: 1, overflowY: "auto", p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      color: "#ff4d4d",
                      fontWeight: 700,
                    }}
                  >
                    <Terminal sx={{ mr: 1, verticalAlign: "middle" }} />
                    Console Logs
                  </Typography>

                  <Box
                    component="pre"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.95rem",
                      color: "#c5c8c6",
                      backgroundColor: "rgba(255,255,255,0.05)",
                      p: 2,
                      borderRadius: 2,
                      minHeight: "300px",
                      overflowY: "auto",
                    }}
                  >
                    {logs.map((line, idx) => (
                      <Typography key={idx}>{line}</Typography>
                    ))}
                  </Box>
                </CardContent>
                <Divider />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    p: 2,
                    gap: 2,
                    backgroundColor: "rgba(255,255,255,0.04)",
                  }}
                >
                  <TextField
                    fullWidth
                    placeholder="Type a command..."
                    variant="outlined"
                    value={command}
                    onChange={(e) => setCommand(e.target.value)}
                    sx={{
                      input: { color: "white" },
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        "& fieldset": {
                          borderColor: "rgba(255,255,255,0.1)",
                        },
                        "&:hover fieldset": {
                          borderColor: "#ff4d4d",
                        },
                      },
                    }}
                  />
                  <IconButton
                    onClick={handleRunCommand}
                    sx={{
                      background:
                        "linear-gradient(135deg, #8b0000, #ff4d4d)",
                      color: "white",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #ff4d4d, #ff9999)",
                      },
                    }}
                  >
                    <Bolt />
                  </IconButton>
                </Box>
              </Card>
            </motion.div>
          </Grid>

          {/* AI Assistant Panel */}
          <Grid item xs={12} md={5}>
            <motion.div {...motionCard}>
              <Card
                sx={{
                  background:
                    "linear-gradient(135deg, rgba(26,31,61,0.85), rgba(10,17,40,0.9))",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 4,
                  height: 500,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  p: 3,
                }}
              >
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      color: "#ff4d4d",
                      fontWeight: 700,
                    }}
                  >
                    <Chat sx={{ mr: 1, verticalAlign: "middle" }} />
                    Nexora AI Assistant
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "rgba(255,255,255,0.85)",
                      lineHeight: 1.7,
                    }}
                  >
                    Get instant insights, code suggestions, and performance tips.
                    Coming soon: real-time API debugging powered by GPT.
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  sx={{
                    border: "2px solid #ff4d4d",
                    color: "#ff4d4d",
                    borderRadius: 3,
                    fontWeight: 600,
                    textTransform: "none",
                    "&:hover": {
                      background: "rgba(255,77,77,0.1)",
                      boxShadow: "0 0 20px rgba(255,77,77,0.3)",
                    },
                    transition: "all 0.3s ease",
                  }}
                  endIcon={<Insights />}
                >
                  Launch AI Console
                </Button>
              </Card>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default DeveloperConsole;
